import React, { useCallback, useEffect, useState } from "react";
import { api, money, fmtDate, fmtDateTime, ago, todayStr, timeLeft, waLink, mapsLink, productName } from "./api";
import { Icon, Badge, Stat, Empty, useToast } from "./Ui.jsx";
import PushToggle from "./PushToggle.jsx";
import PricingSettings from "./PricingSettings.jsx";
import { useAutoRefresh } from "./useAutoRefresh";

// Deben coincidir con el .env del servidor (MAX_WARNINGS y BULK_COMMISSION_PERCENT)
const MAX_WARNINGS = 5;

const isBulk = (o) => Number(o.is_bulk) === 1;
const orderLabel = (o) => (isBulk(o) ? `${o.liters} L granel` : `${o.quantity} × ${productName(o)}`);

const Chips = ({ options, value, onChange }) => (
  <div className="chips">
    {options.map(([id, label]) => (
      <button key={id} className={`chip ${value === id ? "on" : ""}`} onClick={() => onChange(id)}>{label}</button>
    ))}
  </div>
);

/* ---------------- RESUMEN ---------------- */
function Overview({ notify, go }) {
  const [data, setData] = useState(null);

  const load = useCallback(
    () =>
      Promise.all([api("/api/admin/stats"), api("/api/admin/orders"), api("/api/admin/finance")])
        .then(([stats, orders, finance]) => setData({ stats, orders, finance }))
        .catch((e) => notify(e.message, true)),
    [notify]
  );

  // Se actualiza solo cada 30 segundos
    useAutoRefresh(load);

  if (!data) return <Empty>Cargando...</Empty>;
  const { stats, orders, finance } = data;

  const owed = finance.reduce((s, p) => s + Number(p.commission_due), 0);
  const overdue = finance.filter((p) => Number(p.days_left) < 0 && Number(p.commission_due) > 0).length;

  return (
    <>
      {overdue > 0 && (
        <div className="alert alert-bad row-between">
          <span><Icon name="alert" /> {overdue} {overdue === 1 ? "socio con pago vencido" : "socios con pago vencido"}</span>
          <button className="btn btn-sm btn-ghost" onClick={() => go("finance")}>Ver finanzas</button>
        </div>
      )}
      <div className="stats">
        <Stat label="Pedidos pendientes" value={stats.pending} tone={stats.pending ? "warn" : ""} />
        <Stat label="En camino" value={stats.in_transit} />
        <Stat label="Entregados hoy" value={stats.delivered_today} />
        <Stat label="Socios activos" value={stats.active_providers} />
        <Stat label="Comisiones por cobrar" value={money(owed)} />
        <Stat label="Pagos vencidos" value={overdue} tone={overdue ? "bad" : ""} />
      </div>
      <div className="card">
        <div className="card-head">
          <h2>Pedidos recientes</h2>
          <button className="btn btn-sm btn-soft" onClick={() => go("orders")}>Ver todos</button>
        </div>
        {orders.slice(0, 6).map((o) => (
          <div className="list-row" key={o.id}>
            <div>
              <b>{o.customer_name || "Sin nombre"}</b>
              <div className="muted small">{orderLabel(o)} · {o.address}</div>
            </div>
            <div className="right">
              <Badge s={o.status} />
              <div className="muted small">{ago(o.created_at)}</div>
            </div>
          </div>
        ))}
        {orders.length === 0 && <Empty>Aún no hay pedidos.</Empty>}
      </div>
    </>
  );
}

/* ---------------- PEDIDOS ---------------- */
function OrdersTab({ notify }) {
  const [raw, setRaw] = useState("");
  const [form, setForm] = useState(null);
  const [missing, setMissing] = useState([]);
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [quote, setQuote] = useState(null);
  const [cfg, setCfg] = useState(null);

  const load = useCallback(
    () => api("/api/admin/orders").then(setOrders).catch((e) => notify(e.message, true)),
    [notify]
  );
  useAutoRefresh(load);
  useEffect(() => { api("/api/admin/settings").then(setCfg).catch(() => {}); }, []);
  const BULK_PCT = cfg?.bulk_pct ?? 10;

  const analyze = async () => {
    try {
      const p = await api("/api/orders/parse", { method: "POST", body: { raw_text: raw } });
      setMissing(p.missing || []);
      setForm({
        product: /compost/i.test(raw) ? "composta" : "lixiviado",
        customer_name: p.customer_name || "",
        customer_phone: p.customer_phone || "",
        address: p.address || "",
        liters: parseInt(p.product_type, 10) || 5,
        quantity: p.quantity || 1,
        total_price: "",
        pickup_date: "",
      });
    } catch (e) { notify(e.message, true); }
  };

  const compost = form?.product === "composta";
  // Lixiviado: 5 o 10 litros = pedido normal. Cualquier otro número = pedido grande.
  const liters = form ? parseInt(form.liters, 10) || 0 : 0;
  const bulk = !compost && liters > 0 && liters !== 5 && liters !== 10;
  const needsDate = !compost && liters > 10;
  const qty = bulk ? 1 : Math.max(1, parseInt(form?.quantity, 10) || 1);
  const totalLiters = liters * qty;
  const bulkTotal = Number(form?.total_price) || (quote?.ok ? quote.product : 0);
  const compostTotal = (cfg?.compost_price || 0) * qty;

  const hasForm = !!form;
  useEffect(() => {
    if (!hasForm || !liters || compost) { setQuote(null); return; }
    let alive = true;
    const t = setTimeout(() => {
      api(`/api/pricing/quote?liters=${totalLiters}&containers=${bulk ? 0 : qty}`)
        .then((q) => alive && setQuote(q))
        .catch(() => alive && setQuote(null));
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [hasForm, totalLiters, qty, bulk, liters, compost]);

  const minPickup = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  })();

  const publish = async () => {
    let body;
    if (compost) {
      body = {
        raw_text: raw, customer_name: form.customer_name, customer_phone: form.customer_phone,
        address: form.address, product: "composta", quantity: form.quantity,
      };
    } else {
      body = {
        raw_text: raw, customer_name: form.customer_name, customer_phone: form.customer_phone,
        address: form.address, liters,
      };
      if (bulk) {
        body.total_price = form.total_price;
        body.pickup_date = needsDate ? form.pickup_date : null;
      } else {
        body.quantity = form.quantity;
      }
    }
    try {
      await api("/api/admin/orders", { method: "POST", body });
      notify("Pedido publicado");
      setRaw(""); setForm(null); setMissing([]);
      load();
    } catch (e) { notify(e.message, true); }
  };

  const close = async (o) => {
    if (!window.confirm(`¿Ya se recolectó el pedido #${o.id}? Se marcará como entregado y los socios ya no podrán cambiar su aportación.`)) return;
    try {
      await api(`/api/admin/orders/${o.id}/close`, { method: "PATCH" });
      notify("Pedido cerrado");
      load();
    } catch (e) { notify(e.message, true); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const warn = (k) => (missing.includes(k) ? "warn" : "");
  const shown = orders.filter((o) => filter === "all" || o.status === filter);

  return (
    <>
      <div className="card">
        <div className="card-head"><h2>Nuevo pedido</h2></div>
        <p className="muted small" style={{ marginBottom: ".75rem" }}>Pega el mensaje del cliente tal como llegó de Facebook.</p>
        <textarea placeholder="Pega aquí el mensaje..." value={raw} onChange={(e) => setRaw(e.target.value)} />
        {!form && (
          <div className="actions">
            <button className="btn" onClick={analyze} disabled={!raw.trim()}>Analizar mensaje</button>
          </div>
        )}

        {form && (
          <>
            <div className="divider" />
            <Chips value={form.product} onChange={(v) => setForm({ ...form, product: v })}
              options={[["lixiviado", "💧 Lixiviado"], ["composta", "🌱 Composta (saco 20 kg)"]]} />
            {missing.length > 0 && (
              <div className="alert alert-warn" style={{ marginTop: ".75rem" }}>No pude detectar todos los datos. Completa los campos marcados.</div>
            )}
            <div className="form-grid" style={{ marginTop: ".75rem" }}>
              <div className="field"><label>Cliente</label><input className={warn("customer_name")} value={form.customer_name} onChange={set("customer_name")} /></div>
              <div className="field"><label>Teléfono</label><input className={warn("customer_phone")} value={form.customer_phone} onChange={set("customer_phone")} inputMode="numeric" /></div>
              <div className="field span-2"><label>Dirección</label><input className={warn("address")} value={form.address} onChange={set("address")} /></div>

              {compost ? (
                <div className="field"><label>Cantidad de sacos (20 kg c/u)</label><input type="number" min="1" value={form.quantity} onChange={set("quantity")} /></div>
              ) : (
                <>
                  <div className="field">
                    <label>Litros</label>
                    <input type="number" min="1" className={warn("product_type")} value={form.liters} onChange={set("liters")} />
                  </div>
                  {!bulk ? (
                    <div className="field"><label>Cantidad de envases</label><input type="number" min="1" value={form.quantity} onChange={set("quantity")} /></div>
                  ) : (
                    <div className="field"><label>Precio total al cliente ($)</label><input type="number" min="1" placeholder={quote?.ok ? String(quote.product) : ""} value={form.total_price} onChange={set("total_price")} /></div>
                  )}
                </>
              )}

              {needsDate && (
                <div className="field">
                  <label>Fecha de recolecta (mínimo mañana)</label>
                  <input type="date" min={minPickup} value={form.pickup_date} onChange={set("pickup_date")} />
                </div>
              )}
            </div>

            {compost && (
              <p className="small" style={{ marginTop: ".75rem" }}>
                Total: <b>{money(compostTotal)}</b> · {money(cfg?.compost_price)} por saco · el socio paga {money(cfg?.compost_commission)} de comisión
              </p>
            )}
            {!compost && quote?.ok && (
              <p className="small" style={{ marginTop: ".75rem" }}>
                Precio calculado: <b>{money(bulk ? quote.product : quote.total)}</b> · {money(quote.pricePerLiter)}/L por {totalLiters} L
                {bulk && " (déjalo vacío para usarlo)"}
              </p>
            )}
            {!compost && quote && !quote.ok && <div className="alert alert-warn">{quote.message}</div>}

            <p className="muted small" style={{ marginTop: ".75rem" }}>
              {compost
                ? "Composta: la toma un socio y tiene 32 horas para entregarla."
                : !bulk
                  ? "Pedido normal (5 o 10 litros): lo toma un socio y tiene 32 horas para entregarlo."
                  : "Pedido grande: los socios verán una barra para elegir cuántos litros aportan."}
              {bulk && bulkTotal > 0 && (
                <> Tú te quedas {money(bulkTotal * BULK_PCT / 100)} ({BULK_PCT}%) y a los socios se les paga {money((bulkTotal * (1 - BULK_PCT / 100)) / liters)} por litro.</>
              )}
            </p>

            <div className="actions">
              <button className="btn" onClick={publish}>Publicar pedido</button>
              <button className="btn btn-ghost" onClick={() => { setForm(null); setMissing([]); }}>Cancelar</button>
            </div>
          </>
        )}
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Pedidos</h2>
          <Chips value={filter} onChange={setFilter}
            options={[["all", "Todos"], ["pendiente", "Pendientes"], ["tomado", "En camino"], ["entregado", "Entregados"]]} />
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr><th>Cliente</th><th>Dirección</th><th>Pedido</th><th>Total</th><th>Estatus</th><th>Socio</th><th>Creado</th><th></th></tr></thead>
            <tbody>
              {shown.map((o) => (
                <tr key={o.id}>
                  <td><b>{o.customer_name || "—"}</b><div className="muted small">{o.customer_phone}</div></td>
                  <td>{o.address}</td>
                  <td className="nowrap">
                    {orderLabel(o)}
                    {isBulk(o) && (
                      <div className="muted small">
                        {Number(o.covered_liters)}/{o.liters} L cubiertos
                        {o.pickup_date && <> · recolecta {fmtDate(o.pickup_date)}</>}
                      </div>
                    )}
                  </td>
                  <td className="nowrap">{money(o.total_price)}</td>
                  <td>
                    <Badge s={o.status} />
                    {Number(o.auto_delivered) === 1 && <span className="tag">auto</span>}
                    {o.status === "tomado" && o.due_at && <div className="muted small">vence en {timeLeft(o.due_at)}</div>}
                  </td>
                  <td>{o.provider_name || "—"}</td>
                  <td className="nowrap muted">{fmtDateTime(o.created_at)}</td>
                  <td className="right nowrap">
                    {isBulk(o) && o.status === "pendiente" && (
                      <button className="btn btn-sm btn-soft" onClick={() => close(o)}>Cerrar</button>
                    )}
                  </td>
                </tr>
              ))}
              {shown.length === 0 && <tr><td colSpan="8"><Empty>No hay pedidos.</Empty></td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/* ---------------- GRANEL ---------------- */
const pickupInfo = (o) => {
  if (o.status !== "pendiente") return null;
  const d = o.days_to_pickup;
  if (d === null || d === undefined) return { tone: "pendiente", text: "sin fecha" };
  if (d < 0) return { tone: "vencido", text: `recolecta vencida hace ${Math.abs(d)} d` };
  if (d === 0) return { tone: "vencido", text: "recolectar HOY" };
  if (d === 1) return { tone: "pendiente", text: "recolecta mañana" };
  return { tone: "pendiente", text: `recolecta en ${d} d` };
};
const dueNow = (o) => o.status === "pendiente" && o.days_to_pickup !== null && o.days_to_pickup <= 0;

function GranelTab({ notify }) {
  const [orders, setOrders] = useState(null);
  const [filter, setFilter] = useState("pendiente");
  const [q, setQ] = useState("");

  const load = useCallback(
    () => api("/api/admin/granel").then(setOrders).catch((e) => notify(e.message, true)),
    [notify]
  );
    useAutoRefresh(load);

  if (!orders) return <Empty>Cargando...</Empty>;

  const open = orders.filter((o) => o.status === "pendiente");
  const missingTotal = open.reduce((s, o) => s + Math.max(Number(o.liters) - o.covered, 0), 0);
  const dueCount = orders.filter(dueNow).length;
  const toPay = orders.flatMap((o) => o.contributions).filter((c) => !c.paid_at)
    .reduce((s, c) => s + Number(c.payout_amount), 0);

  const term = q.trim().toLowerCase();
  const shown = orders.filter((o) => {
    if (filter === "pendiente" && o.status !== "pendiente") return false;
    if (filter === "entregado" && o.status !== "entregado") return false;
    if (filter === "recolectar" && !dueNow(o)) return false;
    if (!term) return true;
    return [o.id, o.customer_name, o.address, o.customer_phone, ...o.contributions.map((c) => c.provider_name)]
      .join(" ").toLowerCase().includes(term);
  });

  const payOne = async (o, c) => {
    if (!window.confirm(`¿Ya le pagaste ${money(c.payout_amount)} a ${c.provider_name} por ${c.liters} L del pedido #${o.id}?`)) return;
    try {
      await api(`/api/admin/bulk/${c.id}/paid`, { method: "PATCH" });
      notify("Pago registrado");
      load();
    } catch (e) { notify(e.message, true); }
  };

  const closeOrder = async (o) => {
    const falta = Math.max(Number(o.liters) - o.covered, 0);
    const warn = falta > 0 ? `\n\nOJO: aún faltan ${falta} L por cubrir.` : "";
    if (!window.confirm(`¿Ya se recolectó el pedido #${o.id}? Se marcará como entregado y los socios ya no podrán cambiar su aportación.${warn}`)) return;
    try {
      await api(`/api/admin/orders/${o.id}/close`, { method: "PATCH" });
      notify("Pedido cerrado");
      load();
    } catch (e) { notify(e.message, true); }
  };

  return (
    <>
      <div className="stats">
        <Stat label="Pedidos abiertos" value={open.length} />
        <Stat label="Litros por cubrir" value={`${missingTotal} L`} tone={missingTotal ? "warn" : ""} />
        <Stat label="Recolectas de hoy o vencidas" value={dueCount} tone={dueCount ? "bad" : ""} />
        <Stat label="Por pagar a socios" value={money(toPay)} />
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Pedidos grandes</h2>
          <Chips value={filter} onChange={setFilter}
            options={[["pendiente", "Abiertos"], ["recolectar", "Por recolectar"], ["entregado", "Cerrados"], ["all", "Todos"]]} />
        </div>
        <p className="muted small" style={{ marginBottom: ".75rem" }}>
          Qué socios aportaron litros, a qué domicilio ir a recolectar y cuánto pagarles.
        </p>
        <input placeholder="Buscar por cliente, socio, dirección o #pedido..." value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      {shown.map((o) => {
        const total = Number(o.liters);
        const missingL = Math.max(total - o.covered, 0);
        const pct = total > 0 ? Math.min(100, Math.round((o.covered / total) * 100)) : 0;
        const info = pickupInfo(o);
        const unpaid = o.contributions.filter((c) => !c.paid_at).length;
        return (
          <div className="card" key={o.id}>
            <div className="card-head">
              <h2>#{o.id} · {total} L · {o.customer_name || "Sin nombre"}</h2>
              <div className="row-between" style={{ gap: ".5rem" }}>
                {info && <Badge s={info.tone} />}
                <Badge s={o.status} />
              </div>
            </div>

            <div className="muted small" style={{ marginBottom: ".4rem" }}>
              Recolecta: <b>{o.pickup_date ? fmtDate(o.pickup_date) : "sin fecha"}</b>
              {info && <> ({info.text})</>} · Cliente paga {money(o.total_price)} · tu comisión {money(o.commission_amount)}
            </div>

            <div style={{ background: "rgba(128,128,128,.2)", borderRadius: 999, height: 10, overflow: "hidden" }}>
              <div style={{ width: `${pct}%`, height: "100%", background: missingL === 0 ? "#16a34a" : "#f59e0b", transition: "width .3s" }} />
            </div>
            <div className="muted small" style={{ margin: ".3rem 0 .5rem" }}>
              {o.covered}/{total} L cubiertos ({pct}%){missingL > 0 ? ` · faltan ${missingL} L` : " · completo ✅"}
            </div>

            <div className="muted small">
              Entrega al cliente: {o.address} ·{" "}
              <a href={mapsLink(o.address)} target="_blank" rel="noreferrer">📍 mapa</a> ·{" "}
              <a href={waLink(o.customer_phone)} target="_blank" rel="noreferrer">{o.customer_phone}</a>
            </div>

            <div className="divider" />
            {o.contributions.map((c) => (
              <div className="list-row" key={c.id}>
                <div>
                  <b>{c.provider_name}</b> · {c.liters} L
                  {c.provider_deleted && <span className="tag">eliminado</span>}
                  <div className="small">
                    {c.provider_address
                      ? <a href={mapsLink(c.provider_address)} target="_blank" rel="noreferrer">📍 {c.provider_address}</a>
                      : <span className="muted">Sin domicilio: agrégalo en Socios</span>}
                  </div>
                  {c.provider_phone && (
                    <div className="small">
                      <a href={`tel:+52${c.provider_phone}`}>{c.provider_phone}</a>{" · "}
                      <a href={waLink(c.provider_phone, `Hola, paso hoy a recoger tus ${c.liters} L (pedido #${o.id}).`)} target="_blank" rel="noreferrer">WhatsApp</a>
                    </div>
                  )}
                </div>
                <div className="right">
                  <b>{money(c.payout_amount)}</b>
                  <div><Badge s={c.paid_at ? "pagado" : "pendiente"} /></div>
                  {!c.paid_at && (
                    <button className="btn btn-sm" style={{ marginTop: ".3rem" }} onClick={() => payOne(o, c)}>Marcar pagado</button>
                  )}
                </div>
              </div>
            ))}
            {o.contributions.length === 0 && <Empty>Ningún socio ha aportado todavía.</Empty>}

            <div className="actions">
              {o.status === "pendiente" && (
                <button className="btn btn-sm btn-soft" onClick={() => closeOrder(o)}>Marcar recolectado y cerrar</button>
              )}
              {o.status === "entregado" && unpaid > 0 && (
                <span className="muted small">Faltan {unpaid} pago(s) a socios</span>
              )}
            </div>
          </div>
        );
      })}
      {shown.length === 0 && <div className="card"><Empty>No hay pedidos grandes.</Empty></div>}
    </>
  );
}

/* ---------------- SOCIOS ---------------- */
const EMPTY = { name: "", email: "", phone: "", address: "", password: "" };

function ProvidersTab({ notify }) {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(
    () => api("/api/admin/providers").then(setList).catch((e) => notify(e.message, true)),
    [notify]
  );
    useAutoRefresh(load);

  const close = () => { setForm(EMPTY); setEditId(null); setShowForm(false); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        const body = { name: form.name, email: form.email, phone: form.phone, address: form.address };
        if (form.password) body.password = form.password;
        await api(`/api/admin/providers/${editId}`, { method: "PATCH", body });
        notify("Socio actualizado");
      } else {
        await api("/api/admin/providers", { method: "POST", body: form });
        notify("Socio registrado");
      }
      close(); load();
    } catch (err) { notify(err.message, true); }
  };

  const edit = (p) => {
    setEditId(p.id);
    setForm({ name: p.name, email: p.email, phone: p.phone || "", address: p.address || "", password: "" });
    setShowForm(true);
  };

  const toggle = async (p) => {
    const status = p.status === "activo" ? "suspendido" : "activo";
    if (!window.confirm(`¿${status === "suspendido" ? "Suspender" : "Reactivar"} a ${p.name}?`)) return;
    try {
      await api(`/api/admin/providers/${p.id}`, { method: "PATCH", body: { status } });
      notify(status === "suspendido" ? `${p.name} suspendido` : `${p.name} reactivado`);
      load();
    } catch (err) { notify(err.message, true); }
  };

  const warn = async (p) => {
    const reason = window.prompt(`Motivo de la advertencia para ${p.name} (opcional):`);
    if (reason === null) return; // canceló
    try {
      const r = await api(`/api/admin/providers/${p.id}/warn`, { method: "POST", body: { reason } });
      notify(r.removed
        ? `${p.name} llegó a ${r.max} advertencias y fue eliminado`
        : `Advertencia ${r.warnings}/${r.max} para ${p.name}`);
      load();
    } catch (err) { notify(err.message, true); }
  };

  const unwarn = async (p) => {
    if (!window.confirm(`¿Quitar la última advertencia de ${p.name}?`)) return;
    try {
      await api(`/api/admin/providers/${p.id}/unwarn`, { method: "POST" });
      notify("Advertencia quitada");
      load();
    } catch (err) { notify(err.message, true); }
  };

  const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <>
      {showForm && (
        <form className="card" onSubmit={submit}>
          <div className="card-head"><h2>{editId ? "Editar socio" : "Nuevo socio"}</h2></div>
          <div className="form-grid">
            <div className="field"><label>Nombre</label><input value={form.name} onChange={f("name")} required /></div>
            <div className="field"><label>Correo (su usuario)</label><input type="email" value={form.email} onChange={f("email")} required /></div>
            <div className="field"><label>Teléfono</label><input value={form.phone} onChange={f("phone")} /></div>
            <div className="field">
              <label>{editId ? "Nueva contraseña (opcional)" : "Contraseña temporal"}</label>
              <input type="text" minLength={6} value={form.password} onChange={f("password")} required={!editId} placeholder="Mínimo 6 caracteres" />
            </div>
            <div className="field span-2">
              <label>Domicilio (donde se recoge el producto)</label>
              <input value={form.address} onChange={f("address")} placeholder="Calle, número, colonia" />
            </div>
          </div>
          <div className="actions">
            <button className="btn">{editId ? "Guardar cambios" : "Registrar socio"}</button>
            <button type="button" className="btn btn-ghost" onClick={close}>Cancelar</button>
          </div>
        </form>
      )}

      <div className="card">
        <div className="card-head">
          <h2>Socios</h2>
          {!showForm && <button className="btn btn-sm" onClick={() => setShowForm(true)}><Icon name="plus" size={16} /> Nuevo socio</button>}
        </div>
        <p className="muted small" style={{ marginBottom: ".75rem" }}>
          Con {MAX_WARNINGS} advertencias el socio se elimina de esta tabla y pierde el acceso.
        </p>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr><th>Nombre</th><th>Correo</th><th>Teléfono</th><th>Domicilio</th><th>Advertencias</th><th>Estatus</th><th></th></tr></thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.id}>
                  <td><b>{p.name}</b></td>
                  <td>{p.email}</td>
                  <td>{p.phone || "—"}</td>
                  <td>{p.address || <span className="muted">Sin domicilio</span>}</td>
                  <td className="nowrap"><b>{p.warnings}/{MAX_WARNINGS}</b></td>
                  <td><Badge s={p.status} /></td>
                  <td className="right nowrap">
                    <button className="btn btn-sm btn-ghost" onClick={() => edit(p)}>Editar</button>{" "}
                    <button className="btn btn-sm btn-danger-soft" onClick={() => warn(p)}><Icon name="alert" size={16} /> Advertencia</button>{" "}
                    {Number(p.warnings) > 0 && <button className="btn btn-sm btn-ghost" onClick={() => unwarn(p)}>Quitar 1</button>}{" "}
                    <button className={`btn btn-sm ${p.status === "activo" ? "btn-danger-soft" : "btn-soft"}`} onClick={() => toggle(p)}>
                      {p.status === "activo" ? "Suspender" : "Reactivar"}
                    </button>
                  </td>
                </tr>
              ))}
              {list.length === 0 && <tr><td colSpan="7"><Empty>Aún no hay socios.</Empty></td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/* ---------------- FINANZAS ---------------- */
function FinanceTab({ notify }) {
  const [rows, setRows] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [bulk, setBulk] = useState([]);

  const load = useCallback(
    () =>
      Promise.all([api("/api/admin/finance"), api("/api/admin/payouts"), api("/api/admin/bulk")])
        .then(([f, p, b]) => { setRows(f); setPayouts(p); setBulk(b); })
        .catch((e) => notify(e.message, true)),
    [notify]
  );
    useAutoRefresh(load);

  const owed = rows.reduce((s, r) => s + Number(r.commission_due), 0);
  const overdue = rows.filter((r) => Number(r.days_left) < 0 && Number(r.commission_due) > 0);
  const toPay = bulk.filter((b) => !b.paid_at).reduce((s, b) => s + Number(b.payout_amount), 0);

  const reset = async (r) => {
    const extra = Number(r.in_progress)
      ? `\n\nTiene ${r.in_progress} pedido(s) en camino: seguirán pendientes y contarán en el ciclo nuevo.`
      : "";
    if (!window.confirm(`¿${r.name} te pagó ${money(r.commission_due)}? Se reiniciará su conteo de 16 días.${extra}`)) return;
    try {
      await api(`/api/admin/providers/${r.id}/reset-cycle`, { method: "POST" });
      notify(`Conteo reiniciado para ${r.name}`);
      load();
    } catch (e) { notify(e.message, true); }
  };

  const payBulk = async (b) => {
    if (!window.confirm(`¿Ya le pagaste ${money(b.payout_amount)} a ${b.provider_name}?`)) return;
    try {
      await api(`/api/admin/bulk/${b.id}/paid`, { method: "PATCH" });
      notify("Pago registrado");
      load();
    } catch (e) { notify(e.message, true); }
  };

  const check = async () => {
    try {
      const r = await api("/api/admin/payments/check-overdue", { method: "POST" });
      notify(`Revisión lista: ${r.suspended} socio(s) suspendido(s), ${r.auto_delivered} pedido(s) cerrado(s) por plazo`);
      load();
    } catch (e) { notify(e.message, true); }
  };

  return (
    <>
      <div className="stats">
        <Stat label="Por cobrar a socios" value={money(owed)} />
        <Stat label="Socios vencidos" value={overdue.length} tone={overdue.length ? "bad" : ""} />
        <Stat label="Por pagar (pedidos grandes)" value={money(toPay)} />
      </div>

      <PricingSettings notify={notify} />

      <div className="card">
        <div className="card-head">
          <h2>Ciclo de 16 días</h2>
          <button className="btn btn-sm btn-ghost" onClick={check}>Revisar ahora</button>
        </div>
        <p className="muted small" style={{ marginBottom: ".75rem" }}>
          Cada socio paga una comisión fija por pedido entregado. Si pasan 16 días sin pagar, se suspende. Cuando te paguen, reinicia su conteo.
        </p>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr><th>Socio</th><th>Ciclo</th><th>Estatus</th><th>Entregas</th><th>Cobrado</th><th>Comisión</th><th></th></tr></thead>
            <tbody>
              {rows.map((r) => {
                const owes = Number(r.commission_due) > 0;
                const late = owes && Number(r.days_left) < 0;
                return (
                  <tr key={r.id}>
                    <td>
                      <b>{r.name}</b>{r.status === "suspendido" && <> <Badge s="suspendido" /></>}
                      {Number(r.in_progress) > 0 && <div className="muted small">{r.in_progress} en camino</div>}
                    </td>
                    <td className="nowrap">{fmtDate(r.cycle_start)} – {fmtDate(r.due_date)}</td>
                    <td>
                      <Badge s={late ? "vencido" : owes ? "pendiente" : "pagado"} />
                      <div className="muted small">
                        {!owes ? "sin deuda" : late ? `vencido hace ${Math.abs(Number(r.days_left))} d` : `${r.days_left} d restantes`}
                      </div>
                    </td>
                    <td>{r.orders_count}</td>
                    <td>{money(r.total_collected)}</td>
                    <td><b>{money(r.commission_due)}</b></td>
                    <td className="right"><button className="btn btn-sm" onClick={() => reset(r)}>Registrar pago y reiniciar</button></td>
                  </tr>
                );
              })}
              {rows.length === 0 && <tr><td colSpan="7"><Empty>Aún no hay socios.</Empty></td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-head"><h2>Pagos a socios · pedidos grandes</h2></div>
        <p className="muted small" style={{ marginBottom: ".75rem" }}>
          Lo que le toca a cada socio por los litros que aportó, ya con tu comisión descontada.
        </p>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr><th>Socio</th><th>Pedido</th><th>Recolecta</th><th>Aportó</th><th>A pagar</th><th>Estatus</th><th></th></tr></thead>
            <tbody>
              {bulk.map((b) => (
                <tr key={b.id}>
                  <td><b>{b.provider_name}</b></td>
                  <td>#{b.order_id} · {b.customer_name || "—"} · {b.order_liters} L</td>
                  <td className="nowrap">{fmtDate(b.pickup_date)}</td>
                  <td>{b.liters} L</td>
                  <td><b>{money(b.payout_amount)}</b></td>
                  <td><Badge s={b.paid_at ? "pagado" : "pendiente"} /></td>
                  <td className="right">{!b.paid_at && <button className="btn btn-sm" onClick={() => payBulk(b)}>Marcar pagado</button>}</td>
                </tr>
              ))}
              {bulk.length === 0 && <tr><td colSpan="7"><Empty>Aún no hay aportaciones.</Empty></td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-head"><h2>Pagos recibidos</h2></div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr><th>Fecha</th><th>Socio</th><th>Ciclo desde</th><th>Entregas</th><th>Comisión</th></tr></thead>
            <tbody>
              {payouts.map((p) => (
                <tr key={p.id}>
                  <td className="nowrap">{fmtDateTime(p.paid_at)}</td>
                  <td><b>{p.provider_name}</b></td>
                  <td className="nowrap">{fmtDate(p.cycle_start)}</td>
                  <td>{p.orders_count}</td>
                  <td><b>{money(p.commission_paid)}</b></td>
                </tr>
              ))}
              {payouts.length === 0 && <tr><td colSpan="5"><Empty>Aún no has registrado pagos.</Empty></td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/* ---------------- DASHBOARD ---------------- */
const TABS = [
  ["overview", "Resumen", "home"],
  ["orders", "Pedidos", "orders"],
  ["granel", "Granel", "box"],
  ["providers", "Socios", "users"],
  ["finance", "Finanzas", "wallet"],
];

export default function AdminDashboard({ user, onLogout }) {
  const [tab, setTab] = useState("overview");
  const [toast, notify] = useToast();
  const title = TABS.find((t) => t[0] === tab)[1];

  return (
    <div className="cz admin">
      <aside className="side">
        <div className="brand"><span className="brand-dot" />Cozobi<small>Admin</small></div>
        <nav className="nav">
          {TABS.map(([id, label, icon]) => (
            <button key={id} className={`nav-item ${tab === id ? "on" : ""}`} onClick={() => setTab(id)}>
              <Icon name={icon} size={20} /><span>{label}</span>
            </button>
          ))}
        </nav>
        <div className="side-foot">
          <span className="who">{user.name}</span>
          <button className="nav-item" onClick={onLogout} aria-label="Salir"><Icon name="logout" size={20} /><span>Salir</span></button>
        </div>
      </aside>

      <main className="content">
                <h1 className="page-title">{title}</h1>
        <div style={{ marginBottom: ".75rem" }}><PushToggle notify={notify} /></div>
        {tab === "overview" && <Overview notify={notify} go={setTab} />}
        {tab === "orders" && <OrdersTab notify={notify} />}
        {tab === "granel" && <GranelTab notify={notify} />}
        {tab === "providers" && <ProvidersTab notify={notify} />}
        {tab === "finance" && <FinanceTab notify={notify} />}
      </main>
      {toast}
    </div>
  );
}